import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  Slider,
} from "@mui/material";
import { SkipPrevious, SkipNext } from "@mui/icons-material";

interface VideoFile {
  file: string;
}

type VideoRef = "planA" | "planB";
type FrameDirection = "forward" | "backward";

interface SynchronizedVideoPlayerProps {
  planAVideo: VideoFile;
  planBVideo: VideoFile;
  planALabel: string;
  planBLabel: string;
  planATestId: string;
  planBTestId: string;
  synchronizeEnabled: boolean;
  onSynchronizeChange: (enabled: boolean) => void;
  muted?: boolean;
}

const SynchronizedVideoPlayer: React.FC<SynchronizedVideoPlayerProps> = ({
  planAVideo,
  planBVideo,
  planALabel,
  planBLabel,
  planATestId,
  planBTestId,
  synchronizeEnabled,
  onSynchronizeChange,
  muted = true,
}) => {
  const planAVideoRef = useRef<HTMLVideoElement>(null);
  const planBVideoRef = useRef<HTMLVideoElement>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeVideo, setActiveVideo] = useState<VideoRef | null>(null);

  // Frame control state
  const [planACurrentFrame, setPlanACurrentFrame] = useState(0);
  const [planATotalFrames, setPlanATotalFrames] = useState(0);
  const [planBCurrentFrame, setPlanBCurrentFrame] = useState(0);
  const [planBTotalFrames, setPlanBTotalFrames] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const frameTime = 1 / 0.5; // Assuming 0.5fps, adjust as needed
  const planBFrameOffset = 2; // Plan B starts at frame 3 but displays as frame 1

  // Frame control functions
  const updateFrameInfo = useCallback(
    (videoRef: VideoRef) => {
      const video =
        videoRef === "planA" ? planAVideoRef.current : planBVideoRef.current;
      if (!video || !video.duration || isNaN(video.duration)) return;

      const totalFrames = Math.floor(video.duration / frameTime);
      const actualCurrentFrame = Math.floor(video.currentTime / frameTime);

      if (videoRef === "planA") {
        setPlanATotalFrames(totalFrames);
        if (!isDragging) {
          setPlanACurrentFrame(actualCurrentFrame);
        }
      } else {
        // For Plan B, adjust total frames and current frame for display
        const displayTotalFrames = totalFrames - planBFrameOffset;
        const displayCurrentFrame = Math.max(
          0,
          actualCurrentFrame - planBFrameOffset,
        );

        setPlanBTotalFrames(displayTotalFrames);
        if (!isDragging) {
          setPlanBCurrentFrame(displayCurrentFrame);
        }
      }
    },
    [frameTime, isDragging, planBFrameOffset],
  );

  const stepFrame = useCallback(
    (direction: FrameDirection, videoRef: VideoRef) => {
      const video =
        videoRef === "planA" ? planAVideoRef.current : planBVideoRef.current;
      if (!video) return;

      if (direction === "forward") {
        video.currentTime = Math.min(
          video.duration,
          video.currentTime + frameTime,
        );
      } else {
        // For Plan B, ensure we don't go below the offset frame
        const minTime = videoRef === "planB" ? planBFrameOffset * frameTime : 0;
        video.currentTime = Math.max(minTime, video.currentTime - frameTime);
      }
    },
    [frameTime, planBFrameOffset],
  );

  const handleSliderChange = useCallback(
    (_: Event, newValue: number | number[], videoRef: VideoRef) => {
      if (typeof newValue !== "number") return;

      const displayFrameNumber = newValue;
      const video =
        videoRef === "planA" ? planAVideoRef.current : planBVideoRef.current;

      if (videoRef === "planA") {
        setPlanACurrentFrame(displayFrameNumber);
      } else {
        setPlanBCurrentFrame(displayFrameNumber);
      }

      if (!video) return;

      // Convert display frame to actual video time
      const actualFrameNumber =
        videoRef === "planB"
          ? displayFrameNumber + planBFrameOffset
          : displayFrameNumber;

      video.currentTime = actualFrameNumber * frameTime;
    },
    [frameTime, planBFrameOffset],
  );

  const handleSliderChangeCommitted = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleSliderChangeStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const syncVideos = useCallback(
    (
      masterVideo: HTMLVideoElement,
      slaveVideo: HTMLVideoElement,
      masterVideoRef: VideoRef,
    ) => {
      if (isSyncing || !synchronizeEnabled) return;

      setIsSyncing(true);

      // Calculate the display frame for the master video
      const masterFrame = Math.floor(masterVideo.currentTime / frameTime);
      let masterDisplayFrame = masterFrame;

      // Adjust display frame for Plan B
      if (masterVideoRef === "planB") {
        masterDisplayFrame = Math.max(0, masterFrame - planBFrameOffset);
      }

      // Calculate target time for slave video based on display frame
      let slaveTargetTime: number;
      if (masterVideoRef === "planA") {
        // Master is Plan A, slave is Plan B - add offset to slave
        slaveTargetTime =
          masterDisplayFrame * frameTime + planBFrameOffset * frameTime;
      } else {
        // Master is Plan B, slave is Plan A - remove offset from slave
        slaveTargetTime = masterDisplayFrame * frameTime;
      }

      // Sync time based on display frames
      const timeDifference = Math.abs(slaveVideo.currentTime - slaveTargetTime);
      if (timeDifference > 0.1) {
        slaveVideo.currentTime = slaveTargetTime;
      }

      // Sync play state
      if (!masterVideo.paused && slaveVideo.paused) {
        slaveVideo.play().catch(console.error);
      } else if (masterVideo.paused && !slaveVideo.paused) {
        slaveVideo.pause();
      }

      setTimeout(() => setIsSyncing(false), 50);
    },
    [isSyncing, synchronizeEnabled, frameTime, planBFrameOffset],
  );

  const handleTimeUpdate = useCallback(
    (videoRef: VideoRef) => {
      // Update frame info for both videos
      updateFrameInfo("planA");
      updateFrameInfo("planB");

      if (isSyncing || !synchronizeEnabled) return;

      const masterVideo =
        videoRef === "planA" ? planAVideoRef.current : planBVideoRef.current;
      const slaveVideo =
        videoRef === "planA" ? planBVideoRef.current : planAVideoRef.current;

      if (masterVideo && slaveVideo && activeVideo === videoRef) {
        syncVideos(masterVideo, slaveVideo, videoRef);
      }
    },
    [isSyncing, synchronizeEnabled, activeVideo, syncVideos, updateFrameInfo],
  );

  const handlePlay = useCallback(
    (videoRef: VideoRef) => {
      setActiveVideo(videoRef);

      if (!synchronizeEnabled) return;

      const masterVideo =
        videoRef === "planA" ? planAVideoRef.current : planBVideoRef.current;
      const slaveVideo =
        videoRef === "planA" ? planBVideoRef.current : planAVideoRef.current;

      if (masterVideo && slaveVideo) {
        syncVideos(masterVideo, slaveVideo, videoRef);
      }
    },
    [synchronizeEnabled, syncVideos],
  );

  const handlePause = useCallback(
    (videoRef: VideoRef) => {
      if (!synchronizeEnabled) return;

      const masterVideo =
        videoRef === "planA" ? planAVideoRef.current : planBVideoRef.current;
      const slaveVideo =
        videoRef === "planA" ? planBVideoRef.current : planAVideoRef.current;

      if (masterVideo && slaveVideo && !isSyncing) {
        slaveVideo.pause();
      }
    },
    [synchronizeEnabled, isSyncing],
  );

  const handleSeeking = useCallback((videoRef: VideoRef) => {
    setActiveVideo(videoRef);
  }, []);

  const handleSeeked = useCallback(
    (videoRef: VideoRef) => {
      if (!synchronizeEnabled) return;

      const masterVideo =
        videoRef === "planA" ? planAVideoRef.current : planBVideoRef.current;
      const slaveVideo =
        videoRef === "planA" ? planBVideoRef.current : planAVideoRef.current;

      if (masterVideo && slaveVideo) {
        syncVideos(masterVideo, slaveVideo, videoRef);
      }
    },
    [synchronizeEnabled, syncVideos],
  );

  const handleLoadedMetadata = useCallback(() => {
    // Ensure both videos start at the same time when sync is enabled
    if (synchronizeEnabled && planAVideoRef.current && planBVideoRef.current) {
      planAVideoRef.current.currentTime = 0;
      planBVideoRef.current.currentTime = planBFrameOffset * frameTime; // Start Plan B at frame 3
    }
    // Update frame info when metadata loads
    updateFrameInfo("planA");
    updateFrameInfo("planB");
  }, [synchronizeEnabled, updateFrameInfo, planBFrameOffset, frameTime]);

  // Initialize Plan B to start at frame 3
  useEffect(() => {
    const planBVideo = planBVideoRef.current;
    if (planBVideo && planBVideo.duration && !isNaN(planBVideo.duration)) {
      planBVideo.currentTime = planBFrameOffset * frameTime;
    }
  }, [planBFrameOffset, frameTime]);

  useEffect(() => {
    const planAVideo = planAVideoRef.current;
    const planBVideo = planBVideoRef.current;

    if (planAVideo && planBVideo) {
      // Plan A event listeners
      const planATimeUpdate = () => handleTimeUpdate("planA");
      const planAPlay = () => handlePlay("planA");
      const planAPause = () => handlePause("planA");
      const planASeeking = () => handleSeeking("planA");
      const planASeeked = () => handleSeeked("planA");

      planAVideo.addEventListener("timeupdate", planATimeUpdate);
      planAVideo.addEventListener("play", planAPlay);
      planAVideo.addEventListener("pause", planAPause);
      planAVideo.addEventListener("seeking", planASeeking);
      planAVideo.addEventListener("seeked", planASeeked);
      planAVideo.addEventListener("loadedmetadata", handleLoadedMetadata);

      // Plan B event listeners
      const planBTimeUpdate = () => handleTimeUpdate("planB");
      const planBPlay = () => handlePlay("planB");
      const planBPause = () => handlePause("planB");
      const planBSeeking = () => handleSeeking("planB");
      const planBSeeked = () => handleSeeked("planB");

      planBVideo.addEventListener("timeupdate", planBTimeUpdate);
      planBVideo.addEventListener("play", planBPlay);
      planBVideo.addEventListener("pause", planBPause);
      planBVideo.addEventListener("seeking", planBSeeking);
      planBVideo.addEventListener("seeked", planBSeeked);
      planBVideo.addEventListener("loadedmetadata", handleLoadedMetadata);

      return () => {
        // Cleanup Plan A listeners
        planAVideo.removeEventListener("timeupdate", planATimeUpdate);
        planAVideo.removeEventListener("play", planAPlay);
        planAVideo.removeEventListener("pause", planAPause);
        planAVideo.removeEventListener("seeking", planASeeking);
        planAVideo.removeEventListener("seeked", planASeeked);
        planAVideo.removeEventListener("loadedmetadata", handleLoadedMetadata);

        // Cleanup Plan B listeners
        planBVideo.removeEventListener("timeupdate", planBTimeUpdate);
        planBVideo.removeEventListener("play", planBPlay);
        planBVideo.removeEventListener("pause", planBPause);
        planBVideo.removeEventListener("seeking", planBSeeking);
        planBVideo.removeEventListener("seeked", planBSeeked);
        planBVideo.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
  }, [
    handleTimeUpdate,
    handlePlay,
    handlePause,
    handleSeeking,
    handleSeeked,
    handleLoadedMetadata,
  ]);

  return (
    <Stack gap={1}>
      {/* Header row with plan labels and synchronize toggle */}
      <Stack direction="row" alignItems="center" gap={2}>
        <Stack flex={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            {planALabel}
          </Typography>
        </Stack>
        <Stack
          flex={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {planBLabel}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={synchronizeEnabled}
                onChange={(e) => onSynchronizeChange(e.target.checked)}
                data-testid="synchronize-toggle"
                color="primary"
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#745de5",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#745de5",
                  },
                }}
              />
            }
            label="Synchronize"
            labelPlacement="start"
            sx={{ margin: 0 }}
          />
        </Stack>
      </Stack>

      {/* Video row */}
      <Stack direction="row" gap={2}>
        <Stack flex={1}>
          <Box
            sx={{
              display: "flex",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <video
              ref={planAVideoRef}
              src={planAVideo.file}
              controls={false}
              autoPlay={false}
              muted={muted}
              data-testid={planATestId}
              style={{
                display: "flex",
                flex: 1,
                width: 1,
                height: "100%",
                maxHeight: "512px",
              }}
            />
          </Box>
        </Stack>

        <Stack flex={1}>
          <Box
            sx={{
              display: "flex",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <video
              ref={planBVideoRef}
              src={planBVideo.file}
              controls={false}
              autoPlay={false}
              muted={muted}
              data-testid={planBTestId}
              style={{
                display: "flex",
                flex: 1,
                width: 1,
                height: "100%",
                maxHeight: "512px",
              }}
            />
          </Box>
        </Stack>
      </Stack>

      {/* Frame Controls Row */}
      <Stack direction="row" gap={2}>
        {/* Plan A Frame Controls */}
        <Stack flex={1}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              py: 1,
              px: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
            }}
          >
            {/* Frame Counter */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Frame {planACurrentFrame} of {planATotalFrames}
            </Typography>

            {/* Frame Slider */}
            <Box sx={{ width: "100%", mb: 1 }}>
              <Slider
                value={planACurrentFrame}
                min={0}
                max={planATotalFrames}
                step={1}
                // @ts-expect-error MUI Slider onChange event type is a union of DOM Event types
                onChange={(event, newValue) =>
                  handleSliderChange(event, newValue, "planA")
                }
                onChangeCommitted={handleSliderChangeCommitted}
                onChangeStart={handleSliderChangeStart}
                data-testid="plan-a-frame-slider"
                sx={{
                  color: "primary.main",
                  "& .MuiSlider-thumb": {
                    width: 16,
                    height: 16,
                  },
                  "& .MuiSlider-track": {
                    height: 4,
                  },
                  "& .MuiSlider-rail": {
                    height: 4,
                  },
                }}
              />
            </Box>

            {/* Frame Navigation Buttons */}
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                onClick={() => stepFrame("backward", "planA")}
                size="small"
                data-testid="plan-a-frame-backward"
                title="Previous frame"
                disabled={planACurrentFrame <= 0}
              >
                <SkipPrevious />
              </IconButton>
              <IconButton
                onClick={() => stepFrame("forward", "planA")}
                size="small"
                data-testid="plan-a-frame-forward"
                title="Next frame"
                disabled={planACurrentFrame >= planATotalFrames}
              >
                <SkipNext />
              </IconButton>
            </Stack>
          </Box>
        </Stack>

        {/* Plan B Frame Controls */}
        <Stack flex={1}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              py: 1,
              px: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
            }}
          >
            {/* Frame Counter */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Frame {planBCurrentFrame} of {planBTotalFrames}
            </Typography>

            {/* Frame Slider */}
            <Box sx={{ width: "100%", mb: 1 }}>
              <Slider
                value={planBCurrentFrame}
                min={0}
                max={planBTotalFrames}
                step={1}
                // @ts-expect-error MUI Slider onChange event type is a union of DOM Event types
                onChange={(event, newValue) =>
                  handleSliderChange(event, newValue, "planB")
                }
                onChangeCommitted={handleSliderChangeCommitted}
                onChangeStart={handleSliderChangeStart}
                data-testid="plan-b-frame-slider"
                sx={{
                  color: "primary.main",
                  "& .MuiSlider-thumb": {
                    width: 16,
                    height: 16,
                  },
                  "& .MuiSlider-track": {
                    height: 4,
                  },
                  "& .MuiSlider-rail": {
                    height: 4,
                  },
                }}
              />
            </Box>

            {/* Frame Navigation Buttons */}
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                onClick={() => stepFrame("backward", "planB")}
                size="small"
                data-testid="plan-b-frame-backward"
                title="Previous frame"
                disabled={planBCurrentFrame <= 0}
              >
                <SkipPrevious />
              </IconButton>
              <IconButton
                onClick={() => stepFrame("forward", "planB")}
                size="small"
                data-testid="plan-b-frame-forward"
                title="Next frame"
                disabled={planBCurrentFrame >= planBTotalFrames}
              >
                <SkipNext />
              </IconButton>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SynchronizedVideoPlayer;
